import { useEffect, useRef, useState } from "react";

const DEFAULT_ERROR_MESSAGE = "Invalid Value";
const DEFAULT_REQUIRED_MESSAGE = "This field is required";

const getValue =
  (obj) =>
  (path = "") => {
    const keys = path.split(".");
    let value = obj;

    for (const key of keys) {
      if (value) {
        if (Array.isArray(value) && !isNaN(key)) {
          // If value is an array and key is a valid index, continue
          const index = parseInt(key);
          value = value[index];
        } else if (typeof value === "object" && key in value) {
          // If value is an object and key exists, continue
          value = value[key];
        } else {
          return undefined; // Return undefined if key is not found
        }
      } else {
        return undefined; // Return undefined if value is null or undefined
      }
    }

    return value;
  };

const updateObject = (obj, location, value) => {
  const path = location.split(".");
  let current = obj;

  for (let i = 0; i < path.length - 1; i++) {
    if (current[path[i]] === undefined) {
      // If the current property doesn't exist, create it
      if (isNaN(path[i + 1])) {
        current[path[i]] = {};
      } else {
        current[path[i]] = [];
      }
    }
    current = current[path[i]];
  }

  current[path[path.length - 1]] = value;

  return { ...obj };
};
/**
 * useForm hook is build for basic form management.
 *  Easy to manage, provides ready-made reusable functions, and inbuilt validation support
 * @param initialFormState use it to initialize your form, Optional
 * @param options form options
 * @returns object of state and helper functions
 */
const useForm = (
  initialFormState = {},
  options = {
    debugging: true,
  }
) => {
  const initialValues = useRef(null);
  const isDirty = useRef(false);
  const dirtyFields = useRef({});
  const [form, setForm] = useState({ ...initialFormState });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    initialValues.current = initialFormState;
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production" && options.debugging)
      console.log({
        form,
        errors,
        isDirty: isDirty.current,
        dirtyFields: dirtyFields.current,
      });
  }, [form, errors]);

  /**
   *
   * @param errors map of form keys and error string, eg {name: "must be > 4 chars", phoneNo: "must be 10 digits"}
   * @returns void
   */
  const setAllErrors = (newErrors) =>
    setErrors((prev) => ({ ...prev, ...newErrors }));

  /**
   * For setting a custom error to a particular key
   * @param key type FormKey
   * @param error error string
   * @returns void
   */
  const setError = (key, error) =>
    setErrors((errors) => ({ ...errors, [key]: error }));

  /**
   * for reinitialized the form state. returns the reinitialized state
   * @param newInitialState New Initial State. if passed, it will replace the actual initial state
   */
  const initForm = (newInitialState) => {
    const newValue = {
      ...(newInitialState ?? initialValues.current ?? {}),
    };
    setForm(newValue);
    setErrors({});
    return newValue;
  };

  /**
   * use it if there is no prebuild function to handle a specific case, or you want to update many values at once.
   * @param fields Partial FormState or updater function, example => {name: "Nikhil", email: "nikhil121sati.5@gmail.com"}
   */
  const setFields = (value) => {
    if (typeof value === "function") {
      setForm((prev) => {
        const val = value(prev);
        isDirty.current = true;
        dirtyFields.current = { ...dirtyFields.current, ...val };
        return { ...prev, ...val };
      });
    } else {
      isDirty.current = true;
      dirtyFields.current = { ...dirtyFields.current, ...value };
      setForm((prev) => ({ ...prev, ...value }));
    }
  };

  /**
   * clearForm will initialize the form to {}.
   * Warning => Clearing the complete state might lead to uncontrolled components
   */
  const clearForm = () => {
    setForm({});
    setErrors({});
  };

  const valueSetter = (key, value, options) => {
    isDirty.current = true;
    let message = "";
    let isValid = false;
    const {
      errorMessage = DEFAULT_ERROR_MESSAGE,
      formatter,
      regex,
      required = false,
      requiredMessage = "",
      validate,
    } = options ?? {};
    if (value) {
      isValid = validate?.(value) ?? regex?.test(value) ?? true;
      message = isValid
        ? ""
        : typeof errorMessage === "function"
        ? errorMessage(value)
        : errorMessage;
    } else {
      if (required) message = requiredMessage || DEFAULT_REQUIRED_MESSAGE;
      else isValid = true;
    }

    const formattedValue = formatter?.(value) ?? value;

    dirtyFields.current = updateObject(
      dirtyFields.current,
      key,
      formattedValue
    );
    setForm((form) => updateObject(form, key, formattedValue));
    setErrors((errors) => ({ ...errors, [key]: isValid ? "" : message }));
  };

  /**
   * @param key any key of the form
   * @param options options for validation and formatting, optional
   * @returns void
   */
  const handleTextBox = (key, options) => (e) => {
    const value = e.target.value;
    valueSetter(key, value, options);
  };

  /**
   * for toggling and un-toggling of any element, eg - radio, switches
   * @param key any key of the form
   * @returns void
   */
  const handleToggle = (key) => () => {
    setForm((form) => ({ ...form, [key]: !form[key] }));
  };

  /**
   * for custom handling, useful in case of dropdowns, autocomplete and multi-select
   * @param key any key of the form
   * @param options options for validation and formatting, optional
   * @returns void
   */
  const handleValue = (key, options) => (value) => {
    valueSetter(key, value, options);
  };

  return {
    values: form,
    getValue: getValue(form),
    initForm,
    clearForm,
    handleTextBox,
    handleToggle,
    handleValue,
    setFields,
    errors,
    setAllErrors,
    setError,
    setErrors,
    isDirty: isDirty.current,
    dirtyFields: dirtyFields.current,
  };
};

export default useForm;
